import { NavigateOptions, To } from 'react-router-dom';
import { AxiosError } from 'axios';
import { SimpleUser, SimpleUserDelegate, SimpleUserOptions } from 'sip.js/lib/platform/web';
import { UserAgent } from 'sip.js';
import { jwtDecode } from 'jwt-decode';

type Decoded = {
  domain: string;
  ramal: string;
  password: string;
  name: string;
  exp: number;
  iat: number;
};

class Api4comScriptService {
  audioElement!: HTMLAudioElement;
  simpleUser!: SimpleUser;

  /**
   * Start api4com script
   */
  async initApi4comScript(
    webphoneToken: string,
    navigate: (to: To, options?: NavigateOptions) => void,
    onTalkingCall: () => void,
    onConnected: () => void,
    onError: (error?: unknown) => void,
    handleStopCall: (isCallback?: boolean) => void,
  ): Promise<void> {
    try {
      this.audioElement = document.getElementById('remoteAudio') as HTMLAudioElement;

      await navigator.mediaDevices.getUserMedia({ audio: true });

      //NOTE - Verificar a melhor forma de autenticação para não expor ramal e senha
      //     - No exemplo fornecido pela api4com faz exatamento assim https://developers.api4com.com/sip-architecture.html

      const decoded: Decoded = jwtDecode(webphoneToken);
      const now = Math.floor(Date.now() / 1000);

      if (!decoded.domain || !decoded.ramal || !decoded.password || !decoded.name || now >= decoded.exp) {
        throw 'usuário não autenticado';
      }

      const simpleUserDelegate: SimpleUserDelegate = {
        onCallAnswered: () => {
          onTalkingCall();
        },

        onCallHangup: () => {
          handleStopCall(true);
        },

        onServerConnect: () => {
          setTimeout(() => onConnected(), 500);
        },

        onCallReceived: () => {
          void this.simpleUser.answer();
        },
      };

      const webSocketServer = `wss://${decoded.domain}:6443`;
      const uri = UserAgent.makeURI(`sip:${decoded.ramal}@${decoded.domain}`);

      const simpleUserOptions: SimpleUserOptions = {
        delegate: simpleUserDelegate,
        media: { remote: { audio: this.audioElement } },
        userAgentOptions: {
          uri: uri,
          displayName: decoded.name,
          authorizationUsername: decoded.ramal,
          authorizationPassword: decoded.password,
          logBuiltinEnabled: false,
        },
      };

      this.simpleUser = new SimpleUser(webSocketServer, simpleUserOptions);

      await this.simpleUser.connect();
      await this.simpleUser.register();
    } catch (error) {
      const { response } = error as AxiosError;
      navigate('/error', {
        state: {
          status: response?.status,
        },
      });
      onError(error);
    }
  }

  /**
   * Send 'call' message to api4com script
   * @param numberToCall the number to call
   */
  startCall(numberToCall: number): Promise<void> {
    const target = `sip:+55${numberToCall}@ramper.com`;
    return this.simpleUser.call(target, { earlyMedia: true });
  }

  /**
   * Send 'hangup' message to api4com script
   */
  stopCall(): void {
    void this.simpleUser.hangup();
  }

  /**
   * Send 'mute' message to api4com script
   */
  mute(): void {
    if (!this.simpleUser.isMuted()) {
      this.simpleUser.unmute();

      return;
    }

    this.simpleUser.mute();
  }

  /**
   * Send 'dtmf' message to api4com script
   * @param value the ura value
   */
  ura(value: number): void {
    if (value) {
      void this.simpleUser.sendDTMF(value.toString());
    }
  }
}

export default new Api4comScriptService();
